/* eslint-disable */
import {
    H6,
    Row,
    Col,
    Card,
    Form,
    Table,
    Button,
    Dialog,
    Spinner,
    CancelButton,
    FormikMultiSelect,
    FormikSimpleSelect,
} from '../../Elements'
import _ from 'lodash'
import Compressor from 'compressorjs'
import React, {useCallback} from 'react'
import { Formik, FieldArray } from 'formik'
import { useDropzone } from 'react-dropzone'
import { useMutation } from '@apollo/client'
import { GET_PRESIGN, INSERT_DOCUMENT } from './queries'
import { Icon, Classes } from '@blueprintjs/core'
import { DocumentUploadSchema } from '@mmg/validation'
import { useIsMobileDevice } from '../../Hooks/mobileDevice'


export const uploadDocument = (jobId, d, getPresign, insertDocuments, setDocumentData, getRelativeKeys, presiteOptions, deleteFailedUploadFile, setFailedFiles, mandatedPrefix = null) =>
    new Promise(async (resolve, reject) => {
        const prefix = mandatedPrefix || d?.document_category?.name
        const preprocessKey = `job/${jobId}/documents/${prefix}${d.file_data.name}`
        let key = `job/${jobId}/documents/${prefix}${d.file_data.name}`
        let processedFile = null

        if (setFailedFiles) setFailedFiles([])
        setDocumentData(existingFiles => existingFiles.concat([{
            key: `${prefix}${d.file_data.name}`,
            relativeId: key,
            isBeingCreated: true
        }]))

        const preprocessFile = () => new Promise((resolve, reject) => {
            if (!d?.file_data?.type?.includes('image') || d?.file_data?.type?.includes('heic')) { //only compress images for now, heic not supported
                processedFile = d.file_data
                resolve(d.file_data)
            } else {
                new Compressor(d.file_data, {
                    quality: Math.min(Math.max(100000 / d.file_data.size, .2), .5), //at least reduce by half, as possible, and reduce to no less than .2 quality
                    convertSize: 100000,
                    convertTypes: ['image/png', 'image/jpg', 'image/jpeg', 'image/webp'],
                    success(result) {
                        processedFile = result
                        key = `job/${jobId}/documents/${prefix}${result.name}`
                        resolve(result)
                    },
                    error(err) {
                        reject(err)
                    }
                })
            }
        })

        preprocessFile()
            .then(async processed => {
                const presignResponse = await getPresign({
                    variables: {
                        key: key,
                        contentType: processed.type
                    }
                })

                if (presignResponse.data.presign.url) {
                    return {presignResponse, file: {...d, file_data: processedFile}}
                } else {
                    throw 'error getting presigned URL'
                }
            })
            .then(async ({presignResponse, file}) => {
                const presignUrl = presignResponse.data.presign.url
                return await fetch(presignUrl, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': file.file_data.type
                    },
                    body: file.file_data
                })
                    .then(res => {
                        if (res.status >= 200 && res.status < 300) {
                            return res
                        } else {
                            throw 'received unexpected document upload status ' + res.status
                        }
                    })
            }, err => {
                throw err
            })
            .then(async () => {
                return await insertDocuments({
                    variables: {
                        documents: {
                            job_id: jobId,
                            document_file_list: [{
                                job_type_job_category: d?.job_type_job_category,
                                file_name: `${prefix}${processedFile.name}`,
                                file_type: processedFile.type,
                                key: key,
                                document_tags: d.document_tags
                            }]
                        }
                    }
                })
            }, err => {
                throw err
            })
            .then(dbInsertResponse => {
                setDocumentData(existingData => {
                    return (getRelativeKeys(dbInsertResponse.data.insertDocuments, presiteOptions))
                        .concat(existingData.filter(d => d.isBeingCreated && d.relativeId !== preprocessKey))
                })

                return resolve()
            }, err => {
                throw err
            })
            .catch(err => {
                // Global catch block entered from errors bubbled up by the individual error handlers

                // remove item from s3
                deleteFailedUploadFile({fullKey: key})
                // remove the item from the ui browser
                setDocumentData(currData => currData.filter(d => d.relativeId !== preprocessKey))
                // add to failedFilesList
                if (setFailedFiles) setFailedFiles(oldState => oldState.concat([{
                    name: d.file_data.name,
                    destination: prefix
                }]))

                reject(err)
            })
    })


const UploadForm = ({ existingFiles = [], values, isUploadDialogOpen, setIsUploadDialogOpen, setFailedFiles,
    isSubmitting, setDocumentData, jobId, getRelativeKeys, presiteOptions, documentTags, deleteFailedUploadFile }) => {
    const initialValues = {
        document_file_list: [],
        last_uploaded_list: [],
        job_id: jobId
    }
    const noFiles = !existingFiles || existingFiles.length === 0
    const [getPresign] = useMutation(GET_PRESIGN)
    const [insertDocuments] = useMutation(INSERT_DOCUMENT)

    const folders = existingFiles.map(ef => (ef.key.substr(0, ef.key.lastIndexOf("/") + 1)))
    const uniqueFolders = [...new Set(folders)]
    const folderOptions = uniqueFolders.filter(f => f !== "").map((f, idx) => ({
        id: idx,
        name: f
    }))

    let fileErrors = false

    const onSubmit = async (props) => {
        if (fileErrors) return

        const model = DocumentUploadSchema.cast(props, { stripUnknown: true })

        setIsUploadDialogOpen(false)
        model.document_file_list.map(d => uploadDocument(model.job_id, d, getPresign, insertDocuments, setDocumentData, getRelativeKeys, presiteOptions, deleteFailedUploadFile, setFailedFiles))
    }

    let duplicateCheck = existingFiles.filter(f => f.document_id)

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={onSubmit}
            validationSchema={DocumentUploadSchema}
            onReset={() => console.log('reset')}
        >
            {({ errors, values, setFieldValue, ...props }) => {
                fileErrors = false
                return <Dialog
                    icon='upload'
                    title='Upload Files'
                    isOpen={isUploadDialogOpen}
                    canOutsideClickClose={false}
                    canEscapeKeyClose={false}
                    isCloseButtonShown={false}
                    enableReinitialize={true}
                    style={{ width: '750px' }}
                >
                    <Form>
                        <div className={Classes.DIALOG_BODY}>
                            <Card>
                                <Row>
                                    <Col xs={12}>
                                        <AttachmentUpload values={values} setFieldValue={setFieldValue} isSubmitting={isSubmitting} />
                                    </Col>
                                </Row>
                                <Row style={{ paddingTop: 20 }}>
                                    <Col xs={8}>
                                        <FormikSimpleSelect
                                            nullable
                                            disabled={noFiles}
                                            name='category_all'
                                            items={folderOptions}
                                        />
                                    </Col>
                                    <Col xs={4}>
                                        <Button
                                            icon='small-tick'
                                            intent='primary'
                                            text='Apply All'
                                            disabled={noFiles || !values.category_all}
                                            onClick={() => {
                                                Array.from(values.document_file_list).forEach(file => {
                                                    file.document_category = values.category_all
                                                    file.job_type_job_category = values.category_all_presite || null
                                                })
                                                setFieldValue('document_file_list', values.document_file_list)
                                            }}
                                            style={{ margin: 0 }}
                                        />
                                    </Col>
                                </Row>
                            </Card>
                            <Card>
                                <Row>
                                    <Col xs={12}>
                                        <H6>Selected Files</H6>
                                    </Col>
                                </Row>
                                <Row>
                                    {noFiles &&
                                        <>
                                            <Col xs={12}>
                                                No Files Selected
                                            </Col>
                                        </>
                                    }
                                    {values?.document_file_list?.length > 0 &&
                                        <FieldArray
                                            name="document_file_list"
                                            label="Upload Files"
                                            render={({ remove }) => (
                                                <Table
                                                    style={{ width: '100%' }}>
                                                    <thead>
                                                        <tr>
                                                            <th>
                                                                File Name
                                                            </th>
                                                            <th>
                                                                Tags
                                                            </th>
                                                            <th>
                                                                Document Category
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {Array.from(values.document_file_list).map((file, idx) => {
                                                            let docCat = file.document_category && file.document_category.name
                                                            let foundItem = duplicateCheck.find((dup) => `${docCat}${file.file_data.name}` === dup.key)
                                                            if (foundItem) fileErrors = true

                                                            return (
                                                                <tr key={`document_file_list-${idx}`}>
                                                                    <td>
                                                                        {file.file_data.name}
                                                                        {foundItem && <p style={{ 'color': 'red' }}>File with this name exists for this category</p>}
                                                                    </td>
                                                                    <td>
                                                                        <FormikMultiSelect
                                                                            items={documentTags}
                                                                            name={`document_file_list[${idx}].document_tags`}
                                                                            key={`document_file_list[${idx}]-document-tags`}
                                                                            activeOnly
                                                                        />
                                                                    </td>
                                                                    <td>
                                                                        <FormikSimpleSelect
                                                                            items={folderOptions}
                                                                            nullable
                                                                            key={`document_file_list-${idx}-document_category`}
                                                                            name={`document_file_list[${idx}].document_category`}
                                                                            dependentName={`document_file_list[${idx}].job_type_job_category`}
                                                                        />
                                                                    </td>
                                                                    <td>
                                                                        <Icon icon='delete' iconSize='20' intent='danger' onClick={() => remove(idx)} />
                                                                    </td>
                                                                </tr>
                                                            )
                                                        })
                                                        }
                                                    </tbody>
                                                </Table>
                                            )}
                                        />
                                    }
                                </Row>
                            </Card>
                        </div>
                        <div className={Classes.DIALOG_FOOTER}>
                            <div className={Classes.DIALOG_FOOTER_ACTIONS} style={{ marginRight: '10px' }}>
                                {isSubmitting && <Spinner size={30} />}
                                <Button type='submit' text='Upload and Categorize' intent='success' icon='upload' disabled={isSubmitting} />
                                <CancelButton onClick={() => setIsUploadDialogOpen(false)} disabled={isSubmitting} />
                            </div>
                        </div>
                    </Form>
                </Dialog>
            }}

        </Formik>

    )
}

export const AttachmentUpload = ({ values, setFieldValue }) => {
    const isMobileDevice = useIsMobileDevice()
    const setFields = (files, dropZoneSource) => {
        setFieldValue('last_uploaded_list', values.last_uploaded_list.filter(f => f?.dropZoneSource !== dropZoneSource).concat(files))

        if (files.length !== 0) {
            if (values.document_file_list.length === 0) {
                Array.from(files).forEach(
                    file_data => {
                        values.document_file_list.push({
                            file_data,
                            job_type_job_category: null
                        })
                    }
                )
            }
            else {
                const newItems = _.differenceWith(files, values.document_file_list, (a, b) => {
                    return a.name === b.file_data.name && b.file_data.type === a.type && b.file_data.size === a.size
                })
                const getKeyedName = (rawName) => {
                    const splitName = rawName.split('.')
                    const extension = splitName.pop()
                    const baseName = splitName.join('.')
                    return `${baseName}_(${Date.now()}).${extension}`
                }
                const allItems = values.document_file_list.map(v => v.file_data).concat(newItems)

                const keyedNewItems = newItems.map(f => {
                    const number = allItems.filter(v => v.name === f.name).length
                    return number <= 1
                        ? f
                        : new File([f], getKeyedName(f.name), { type: f.type })
                })

                keyedNewItems.forEach((file_data, idx) => {
                    values.document_file_list.push({
                        file_data,
                        job_type_job_category: null
                    })
                })
            }
        }
    }

    const FileDropzone = ({ acceptList = '*', multiple = true }) => {
        const onDrop = useCallback(acceptedFiles => {
            acceptedFiles.forEach(f => f.dropZoneSource = acceptList)
            setFields(acceptedFiles)
        }, [])
        const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop })

        const InactiveDragTextNode = () => acceptList === 'image/*'
            ? <p>Drag and drop <strong>images</strong> here, or <strong>click</strong> to select <strong>images</strong></p>
            : acceptList === 'video/*'
                ? <p>Drag and drop <strong>a video</strong> here, or <strong>click</strong> to select <strong>a video</strong></p>
                : <p>Drag and drop some files here, or <strong>click</strong> to select files</p>

        const ActiveDragTextNode = () => acceptList === 'image/*'
            ? <p>Drop the <strong>images</strong> here ...</p>
            : acceptList === 'video/*'
                ? <p>Drop <strong>a video</strong> here ...</p>
                : <p>Drop the files here ...</p>

        return <div {...getRootProps()} className='dropzone'>
            <input {...getInputProps()} multiple={multiple} accept={acceptList} />
            {isDragActive
                ? <ActiveDragTextNode />
                : <InactiveDragTextNode />
            }
        </div>
    }

    const MobileDeviceDropzoneRow = () => <Row>
        <Col xs={6}><FileDropzone acceptList='image/*' /></Col>
        <Col xs={6}><FileDropzone acceptList='video/*' multiple={false} /></Col>
    </Row>

    return isMobileDevice
        ? <MobileDeviceDropzoneRow />
        : <FileDropzone />
}

export default UploadForm
